import {updateCurrentState} from '@github-ui/history'
import {LRUMap} from '@github-ui/lru-map'
import {
  findReactApp,
  type NavigableReactApp,
  resolveReactAppNavigation,
  type ResolvedReactAppNavigation,
  startReactAppNavigation,
} from '@github-ui/react-app-navigation'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav-events'
import {hasNavigationContextHandler} from '@github-ui/react-app-navigation/navigation-context-handler'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {reloadPage} from './reload-page'
import {canRepoSoftNavigate} from './utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import isHashNavigation from '@github-ui/is-hash-navigation'
import {getCurrentAppName, sendCustomMetric} from '@github-ui/stats'
import {getCurrentRouteId} from '@github-ui/stats-metadata'
type RepoRouteHandle = {repoSubAppName?: string}

export const bfCache = new LRUMap<string, Map<string, Element>>({size: 20})

function replaceTurboElements({idsToRemove, reactApp}: {idsToRemove: string[]; reactApp: NavigableReactApp}) {
  const removedElements = new Map()

  if (idsToRemove.length === 0) {
    reactApp.hidden = false
    return removedElements
  }

  // Batch DOM changes to prevent flash
  requestAnimationFrame(() => {
    reactApp.hidden = false

    for (const elementId of idsToRemove) {
      const element = document.getElementById(elementId)

      if (element) {
        // Replace elements with hidden placeholders so we know where to restore them later
        const placeholder = document.createElement('div')
        placeholder.id = elementId
        placeholder.hidden = true
        element.replaceWith(placeholder)

        removedElements.set(elementId, element)
      }
    }
  })

  return removedElements
}

export function reactNavigateIfPossible(event: TurboClickEvent | TurboFrameClickEvent) {
  if (!(event.target instanceof HTMLElement)) return

  const target = event.target

  const reactAppName = target.getAttribute('data-react-nav')

  if (!reactAppName) return false

  // Try to use React navigation if the react app is loaded
  const reactApp = findReactApp(reactAppName)

  if (!reactApp) return false

  const anchorId = target.getAttribute('data-react-nav-anchor')

  const firedReactNav = anchorId
    ? clickOnReactAnchor({event, reactApp, anchorId})
    : imperativelyNavigateToReactRoute({event, reactApp})

  if (!firedReactNav) return false

  const idsToRemove = target.getAttribute('data-react-nav-remove')?.split(',') || []

  if (idsToRemove.length === 0) return firedReactNav

  const currentHref = window.location.href
  updateCurrentState({restoreTurboElements: {appName: reactAppName, idsToRestore: idsToRemove}})

  const handleSoftNavEnd = () => {
    updateCurrentState({restoreReactElements: {appName: reactAppName, idsToRemove}})
    const removedElements = replaceTurboElements({idsToRemove, reactApp})

    if (removedElements) bfCache.set(currentHref, removedElements)

    document.removeEventListener(SOFT_NAV_STATE.ERROR, handleSoftNavError)
  }

  const handleSoftNavError = () => {
    document.removeEventListener(SOFT_NAV_STATE.END, handleSoftNavEnd)
  }

  document.addEventListener(SOFT_NAV_STATE.END, handleSoftNavEnd, {once: true})
  document.addEventListener(SOFT_NAV_STATE.ERROR, handleSoftNavError, {once: true})

  return firedReactNav
}

export function repoNavigateIfPossible(event: TurboClickEvent | TurboFrameClickEvent) {
  if (!(event.target instanceof HTMLElement)) return false
  if (isHashNavigation(location.href, event.detail.url)) return false
  if (!isFeatureEnabled('repo_app_turbo')) return false
  // Turbo Stream links request a stream rather than a page, so Turbo handles them.
  if (event.target.hasAttribute('data-turbo-stream')) return false
  // The global navigation registers a handler that refreshes its context for routes in another repository.
  const allowOtherRepository = isFeatureEnabled('global_nav_context_refresh') && hasNavigationContextHandler()
  if (!canRepoSoftNavigate(event.detail.url, {allowOtherRepository})) return false

  const reactApp = findReactApp('repo')

  if (!reactApp) return false

  const navigation = resolveReactAppNavigation({destination: event.detail.url, app: reactApp, requireLeafRoute: true})
  if (!navigation) return false

  const leafRoute = navigation.matchedRoutes.at(-1)?.route
  const repoSubAppName = (leafRoute?.handle as RepoRouteHandle | undefined)?.repoSubAppName
  const toRouteId = navigation.matchedRoutes.findLast(match => match.route.id)?.route.id

  if (!repoSubAppName) return false
  const fromRouteId = getCurrentRouteId()
  if (!startReactNavigationAndPreventTurbo({event, navigation})) return false

  sendCustomMetric(
    {
      name: 'REPO_NAVIGATION_TURBO_PROMOTED',
      value: 1,
      tags: {
        from: getCurrentAppName(),
        to: repoSubAppName,
        ...(fromRouteId ? {fromRouteId} : {}),
        ...(toRouteId ? {toRouteId} : {}),
      },
      requestUrl: window.location.href,
    },
    false,
    1,
  )

  return true
}

function clickOnReactAnchor({
  event,
  anchorId,
}: {
  event: TurboClickEvent | TurboFrameClickEvent
  reactApp: NavigableReactApp
  anchorId: string
}) {
  const anchor = document.getElementById(anchorId) as HTMLAnchorElement | null

  if (!anchor) return false

  anchor.click()
  preventTurboNavigation({event})
  return true
}

function imperativelyNavigateToReactRoute({
  event,
  reactApp,
}: {
  event: TurboClickEvent | TurboFrameClickEvent
  reactApp: NavigableReactApp
}): boolean {
  const navigation = resolveReactAppNavigation({destination: event.detail.url, app: reactApp})
  return !!navigation && startReactNavigationAndPreventTurbo({event, navigation})
}

function startReactNavigationAndPreventTurbo({
  event,
  navigation,
}: {
  event: TurboClickEvent | TurboFrameClickEvent
  navigation: ResolvedReactAppNavigation
}) {
  if (!startReactAppNavigation(navigation)) return false

  try {
    preventTurboNavigation({event})
    return true
  } catch {
    return false
  }
}

function preventTurboNavigation({event}: {event: TurboClickEvent | TurboFrameClickEvent}) {
  event.preventDefault() // prevent Turbo navigation
  event.detail.originalEvent?.preventDefault() // prevent the original link click
}

function restoreTurboElements({appName, idsToRestore}: {appName: string; idsToRestore?: string[]}) {
  const reactApp = document.querySelector<HTMLElement>(`react-app[app-name="${appName}"]`)

  const cache = bfCache.get(window.location.href)
  if (!cache && idsToRestore && idsToRestore.length > 0) {
    return reloadPage()
  }

  if (reactApp) reactApp.hidden = true

  if (!cache) return

  requestAnimationFrame(() => {
    // restore Rails cached elements
    for (const [elementId, element] of cache.entries()) {
      const placeholder = document.getElementById(elementId)
      if (placeholder) placeholder.replaceWith(element)
    }
  })
}

ssrSafeWindow?.addEventListener('popstate', ({state}) => {
  if (!state) return

  if (state.restoreTurboElements) {
    return restoreTurboElements(state.restoreTurboElements)
  }

  // Wait for React to render before replacing elements to avoid a blank flash
  if (state.restoreReactElements) {
    document.addEventListener(
      SOFT_NAV_STATE.REACT_DONE,
      () => {
        const reactApp = findReactApp(state.restoreReactElements.appName)
        if (reactApp) {
          replaceTurboElements({
            idsToRemove: state.restoreReactElements.idsToRemove,
            reactApp,
          })
        }
      },
      {once: true},
    )
  }
})
